import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { getToken } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Send, Loader2, FileText, Sparkles, ScanText, Eraser } from "lucide-react";
import ModelPicker from "@/components/chat/ModelPicker";
import AnswerMeta from "@/components/chat/AnswerMeta";
import DocVisuals from "@/components/chat/DocVisuals";
import ExportMenu from "@/components/chat/ExportMenu";

const CITATION = /(\[[^\]]+?,\s*(?:Pages?|Sections?)\s+\d+(?:\s*[-–,]\s*(?:(?:Pages?|Sections?)\s+)?\d+)*\])/g;
const IS_CITATION = /^\[[^\]]+?,\s*(?:Pages?|Sections?)\s+\d+(?:\s*[-–,]\s*(?:(?:Pages?|Sections?)\s+)?\d+)*\]$/;

export default function DocumentChat() {
  const { docIds } = useParams();
  const nav = useNavigate();
  const ids = useMemo(() => docIds.split(","), [docIds]);
  const [docs, setDocs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [model, setModel] = useState(localStorage.getItem("documind_model") || "auto");
  const [mobileView, setMobileView] = useState("chat");
  const [jump, setJump] = useState(null);

  const goToSource = (citation) => {
    const m = citation.match(/^\[(.+?),\s*(Page|Section)s?\s+(\d+)/);
    if (!m) return;
    const name = m[1].trim().toLowerCase();
    const target = docs.find((d) => d.filename.toLowerCase() === name)
      || docs.find((d) => name.includes(d.filename.toLowerCase()) || d.filename.toLowerCase().includes(name))
      || activeDoc;
    if (!target) return;
    setActiveDoc(target);
    setJump({ docId: target.id, unit: m[2].toLowerCase(), n: Number(m[3]), t: Date.now() });
    setMobileView("doc");
  };
  const bottomRef = useRef(null);

  const chooseModel = (m) => { setModel(m); localStorage.setItem("documind_model", m); };
  const patchLast = (patch) => setMessages((m) => {
    const copy = [...m];
    copy[copy.length - 1] = { ...copy[copy.length - 1], ...patch(copy[copy.length - 1]) };
    return copy;
  });

  useEffect(() => {
    (async () => {
      try {
        const list = await Promise.all(ids.map((id) => api.get(`/documents/${id}`).then((r) => r.data)));
        setDocs(list);
        setActiveDoc(list[0]);
        const h = await api.get(`/chat/history?document_ids=${[...ids].sort().join(",")}`);
        setMessages(h.data);
      } catch {
        toast.error("Failed to load");
        nav("/app");
      }
    })();
  }, [ids, nav]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const processing = docs.filter((d) => d.status === "processing");
  useEffect(() => {
    if (!processing.length) return;
    const t = setTimeout(async () => {
      try {
        const list = await Promise.all(ids.map((id) => api.get(`/documents/${id}`).then((r) => r.data)));
        setDocs(list);
        setActiveDoc((a) => list.find((d) => d.id === a?.id) || list[0]);
        if (!list.some((d) => d.status === "processing")) toast.success("Scanned pages are ready — ask away");
      } catch (err) { console.warn("Doc poll failed", err); }
    }, 5000);
    return () => clearTimeout(t);
  }, [processing.length, docs, ids]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming || processing.length) return;
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", content: text }, { id: crypto.randomUUID(), role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ message: text, document_ids: ids, model }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Chat failed");
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          try {
            const j = JSON.parse(p.slice(5).trim());
            if (j.delta) {
              patchLast((last) => ({ content: last.content + j.delta }));
            } else if (j.meta) {
              patchLast(() => ({ model_label: j.meta.label, auto: j.meta.auto }));
            } else if (j.grounded !== undefined) {
              patchLast(() => ({ grounded: j.grounded }));
            } else if (j.error) {
              toast.error(j.error);
            }
          } catch (err) { console.warn("Skipped malformed stream event", err); }
        }
      }
    } catch (e) {
      toast.error(e.message);
      setMessages((m) => m.slice(0, -2));
      setInput(text);
    } finally {
      setStreaming(false);
    }
  };

  const suggestions = [
    "Summarize this document in 150 words.",
    "What are the three most important findings?",
    "List all the limitations the author mentions, with citations.",
  ];

  const resetChat = async () => {
    if (!window.confirm("Start a new conversation? The messages in this chat will be deleted and the assistant will forget them.")) return;
    try {
      await api.delete(`/chat/history?document_ids=${[...ids].sort().join(",")}`);
      setMessages([]);
      toast.success("Fresh start — ask away");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't clear the chat");
    }
  };

  const bold = (text, key) => text.split(/(\*\*[^*]+\*\*)/g).map((t, j) =>
    /^\*\*[^*]+\*\*$/.test(t) ? <strong key={`${key}-${j}`}>{t.slice(2, -2)}</strong> : <span key={`${key}-${j}`}>{t}</span>);
  const render = (content) => content.split(CITATION).map((part, i) =>
    IS_CITATION.test(part)
      ? <button key={i} type="button" data-testid="citation-link" onClick={() => goToSource(part)} title="Show this in the document"
          className="font-mono-label text-primary mx-0.5 align-super text-[0.62rem] underline decoration-dotted underline-offset-2 hover:bg-primary hover:text-primary-foreground px-0.5 rounded-sm transition-colors">{part}</button>
      : bold(part, i));

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      <header className="border-b border-border bg-background">
        <div className="max-w-full px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <Button variant="ghost" size="sm" data-testid="chat-back" onClick={() => nav("/app")}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Library
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="font-medium truncate">{docs.map((d) => d.filename).join(" · ")}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" data-testid="chat-new-conversation" disabled={messages.length === 0 || streaming} onClick={resetChat} className="rounded-sm">
              <Eraser className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">New conversation</span>
            </Button>
            <ExportMenu docIds={ids} disabled={messages.length === 0 || streaming} />
            <div className="font-mono-label text-muted-foreground hidden sm:block whitespace-nowrap">Chatting with {docs.length} doc{docs.length > 1 ? 's' : ''}</div>
          </div>
        </div>
      </header>

      <div className="lg:hidden grid grid-cols-2 border-b border-border bg-background" data-testid="mobile-view-toggle">
        {[["doc", "Document"], ["chat", "Chat"]].map(([k, l]) => (
          <button key={k} data-testid={`mobile-view-${k}`} onClick={() => setMobileView(k)}
            className={`font-mono-label py-3 transition-colors ${mobileView === k ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted"}`}>
            {l}{k === "chat" && streaming && mobileView === "doc" ? " · answering…" : ""}
          </button>
        ))}
      </div>

      <div className="flex-1 grid lg:grid-cols-2 min-h-0 overflow-hidden">
        {/* Left: PDF */}
        <div className={`${mobileView === "doc" ? "flex" : "hidden"} lg:flex lg:border-r border-border bg-muted min-h-0 flex-col overflow-hidden`}>
          {docs.length > 1 && (
            <div className="flex flex-wrap gap-1 p-3 border-b border-border bg-background">
              {docs.map((d) => (
                <button
                  key={d.id}
                  data-testid={`doc-tab-${d.id}`}
                  onClick={() => setActiveDoc(d)}
                  className={`font-mono-label px-3 py-1.5 border ${activeDoc?.id === d.id ? 'bg-foreground text-background border-foreground' : 'border-border hover:bg-muted'}`}
                >
                  {d.filename.length > 24 ? d.filename.slice(0, 24) + "…" : d.filename}
                </button>
              ))}
            </div>
          )}
          {activeDoc && <DocVisuals key={activeDoc.id} doc={activeDoc} jump={jump?.docId === activeDoc.id ? jump : null} />}
        </div>

        {/* Right: Chat */}
        <div className={`${mobileView === "chat" ? "flex" : "hidden"} lg:flex flex-col min-h-0`}>
          <div className="flex-1 overflow-y-auto p-6 lg:p-8 space-y-4">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <Sparkles strokeWidth={1.2} className="w-10 h-10 text-primary mb-4" />
                <h3 className="font-serif-display text-2xl mb-3">Ask anything about your document.</h3>
                <p className="text-sm text-muted-foreground max-w-sm mb-8">Every answer comes with page citations back to the source. Try one of these:</p>
                <div className="grid gap-2 w-full max-w-md">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      data-testid="chat-suggestion"
                      onClick={() => setInput(s)}
                      className="text-left text-sm border border-border px-4 py-3 hover:border-foreground hover:bg-muted"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id || `${m.role}-${m.created_at}`} className={`chat-block ${m.role === 'user' ? 'user' : ''}`}>
                <div className="font-mono-label mb-2 text-muted-foreground">{m.role === 'user' ? 'You' : 'DocuMind'}</div>
                <div className={`text-[15px] leading-relaxed whitespace-pre-wrap answer-prose ${streaming && i === messages.length - 1 ? 'caret' : ''}`}>
                  {render(m.content)}
                </div>
                {m.role === 'assistant' && !(streaming && i === messages.length - 1 && m.grounded === undefined && !m.model_label) && <AnswerMeta message={m} />}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-border p-4 bg-background">
            {processing.length > 0 && (
              <div data-testid="chat-ocr-notice" className="mb-3 flex items-center gap-2 border border-primary bg-primary/5 px-3 py-2 text-sm text-primary">
                <ScanText className="w-4 h-4 animate-pulse shrink-0" />
                Reading scanned pages with OCR ({processing.map((d) => `${d.ocr_done || 0}/${d.ocr_pending || "?"}`).join(", ")}) — chat unlocks the moment it's done.
              </div>
            )}
            <div className="flex items-center justify-between mb-2 gap-3">
              <ModelPicker value={model} onChange={chooseModel} disabled={streaming} />
              <span className="text-xs text-muted-foreground hidden md:inline">{model === "auto" ? "Simple questions go to a fast model, hard ones to a stronger one" : "Answers come from the model you picked"}</span>
            </div>
            <div className="flex gap-2 items-end">
              <Textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask about the document…"
                rows={2}
                className="rounded-sm resize-none"
              />
              <Button data-testid="chat-send" onClick={send} disabled={streaming || !input.trim() || processing.length > 0} className="h-auto rounded-sm bg-foreground text-background hover:bg-primary btn-invert">
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Press Enter to send · Shift+Enter for newline</p>
          </div>
        </div>
      </div>
    </div>
  );
}
